import { useParams } from 'react-router-dom'
import { TrendingUp, TrendingDown, Minus, BarChart2 } from 'lucide-react'
import { useApi, asArray } from '../hooks/useApi'

interface PositionEntry {
  id: number
  position: number | null
  url: string | null
  checkedAt: string
}

interface HistoryData {
  keyword: string
  device: string
  positions: PositionEntry[]
}

function PositionDot({ position }: { position: number | null }) {
  if (position === null) {
    return <span className="text-[#555A73] font-medium text-sm">Non classé</span>
  }
  if (position <= 3) {
    return <span className="text-emerald-400 font-bold text-sm">#{position}</span>
  }
  if (position <= 10) {
    return <span className="text-blue-400 font-semibold text-sm">#{position}</span>
  }
  if (position <= 30) {
    return <span className="text-yellow-500 font-semibold text-sm">#{position}</span>
  }
  return <span className="text-[#8B8FA7] text-sm">#{position}</span>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function RankingDetail() {
  const { projectId, keywordId } = useParams<{ projectId: string; keywordId: string }>()

  const { data, loading, error } = useApi<HistoryData>(
    `/api/keywords/${keywordId}/history`
  )

  if (error) {
    return (
      <div className="bg-light-body text-[#1B2559] flex items-center justify-center py-20">
        <p className="text-[#555A73]">Erreur : {error}</p>
      </div>
    )
  }

  const positions = asArray(data?.positions)
  const latest = positions[positions.length - 1]
  const previous = positions[positions.length - 2]
  let delta: number | null = null
  if (latest?.position !== undefined && previous?.position !== undefined) {
    if (latest.position !== null && previous.position !== null) {
      delta = previous.position - latest.position
    }
  }

  return (
    <div className="text-[#1B2559] space-y-6">
      {/* Keyword header */}
      {data && (
        <div className="mb-2">
          <p className="font-semibold text-[#1B2559] leading-tight">{data.keyword}</p>
          <p className="text-xs text-[#555A73] leading-tight mt-0.5">Historique — {data.device}</p>
        </div>
      )}
        {/* Latest position card */}
        {!loading && data && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="bg-white border border-[#E2E4ED] rounded-2xl p-5">
              <p className="text-xs text-[#555A73] font-medium uppercase tracking-wider mb-1">Position actuelle</p>
              {latest ? (
                <PositionDot position={latest.position} />
              ) : (
                <span className="text-[#555A73] text-sm">—</span>
              )}
            </div>

            <div className="bg-white border border-[#E2E4ED] rounded-2xl p-5">
              <p className="text-xs text-[#555A73] font-medium uppercase tracking-wider mb-1">Évolution</p>
              <div className="flex items-center gap-1">
                {delta === null ? (
                  <span className="text-[#555A73] text-sm">—</span>
                ) : delta > 0 ? (
                  <>
                    <TrendingUp className="w-4 h-4 text-emerald-400" strokeWidth={1.25} />
                    <span className="text-emerald-400 font-semibold text-sm">+{delta}</span>
                  </>
                ) : delta < 0 ? (
                  <>
                    <TrendingDown className="w-4 h-4 text-red-400" strokeWidth={1.25} />
                    <span className="text-red-400 font-semibold text-sm">{delta}</span>
                  </>
                ) : (
                  <>
                    <Minus className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />
                    <span className="text-[#555A73] text-sm">Stable</span>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#E2E4ED] rounded-2xl p-5 col-span-2 sm:col-span-1">
              <p className="text-xs text-[#555A73] font-medium uppercase tracking-wider mb-1">Relevés</p>
              <span className="text-[#1B2559] font-semibold text-sm">{positions.length}</span>
            </div>
          </div>
        )}

        {/* Chart placeholder */}
        <div className="bg-white border border-[#E2E4ED] rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />
            <p className="text-sm font-medium text-[#1B2559]">Évolution de la position</p>
          </div>
          <div className="flex items-center justify-center h-32 rounded-xl bg-[#F4F5FA] border border-dashed border-[#E2E4ED]">
            <p className="text-xs text-[#555A73]">Graphique ECharts — à venir</p>
          </div>
        </div>

        {/* History list */}
        <div className="bg-white border border-[#E2E4ED] rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#E2E4ED]">
            <p className="text-sm font-medium text-[#1B2559]">Historique des relevés</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-5 h-5 border-2 border-[#555A73] border-t-orion-primary rounded-full animate-spin" />
            </div>
          ) : positions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-[#555A73] text-sm">Aucun relevé disponible.</p>
              <p className="text-xs text-[#555A73] mt-1">Les positions seront trackées automatiquement.</p>
            </div>
          ) : (
            <ul className="divide-y divide-dark-border">
              {[...positions].reverse().map(entry => (
                <li key={entry.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-[#F4F5FA]/40 transition-colors">
                  <div className="shrink-0 text-xs text-[#555A73] w-28">{formatDate(entry.checkedAt)}</div>
                  <div className="shrink-0 w-20">
                    <PositionDot position={entry.position} />
                  </div>
                  <div className="min-w-0 flex-1">
                    {entry.url ? (
                      <a
                        href={entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#8B8FA7] hover:text-orion-primary transition-colors truncate block"
                      >
                        {entry.url.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    ) : (
                      <span className="text-xs text-[#555A73]">—</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
    </div>
  )
}
